import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import type { SendPasswordResetResponse } from "@/types/edge-functions";

type Mode = "login" | "forgot" | "forgot-sent";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
    } else {
      navigate(email.toLowerCase().endsWith("@trydiscorun.com") ? "/admin" : "/");
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await invokeEdgeFunction<SendPasswordResetResponse>(
        "send-password-reset",
        { email: forgotEmail },
        { skipAuth: true }
      );
    } catch {
      // Silently ignore errors — never reveal whether the email exists
    }

    // Always show success — never leak whether the email exists
    setMode("forgot-sent");
    setIsLoading(false);
  };

  const switchToForgot = () => {
    setForgotEmail(email); // pre-fill if they already typed their email
    setError(null);
    setMode("forgot");
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center relative overflow-hidden">
      {/* Dot grid background */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:24px_24px]" />

      {/* Indigo glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-indigo-500/10 blur-[100px] rounded-full" />

      {/* Card container */}
      <div className="relative z-10 w-full max-w-sm mx-auto px-4">
        {/* Logo mark */}
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
          <Zap className="w-5 h-5 text-white" />
        </div>

        {/* Wordmark */}
        <p className="text-2xl font-bold font-display bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent text-center">
          DiscoRun
        </p>

        {/* Tagline */}
        <p className="text-sm text-white/40 text-center mt-1 mb-8">AI enabled procurement</p>

        {/* Form card */}
        <div
          className="rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl p-8"
          style={{
            boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          {mode === "login" && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="email"
                  className="block text-[11px] uppercase tracking-wider font-medium text-white/50"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@company.com"
                  required
                  className="bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 focus:border-indigo-500/60 focus:ring-0 focus:ring-offset-0 rounded-lg"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-[11px] uppercase tracking-wider font-medium text-white/50"
                  >
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={switchToForgot}
                    className="text-[11px] text-indigo-400/70 hover:text-indigo-400 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 focus:border-indigo-500/60 focus:ring-0 focus:ring-offset-0 rounded-lg pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-400 hover:to-cyan-400 text-white font-medium transition-all duration-200 hover:shadow-[0_0_20px_rgba(99,102,241,0.35)] border-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setMode("login");
                    setError(null);
                  }}
                  className="flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/70 transition-colors mb-4"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to sign in
                </button>
                <h2 className="text-sm font-semibold text-white/80 mb-1">Reset your password</h2>
                <p className="text-xs text-white/40">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="forgot-email"
                  className="block text-[11px] uppercase tracking-wider font-medium text-white/50"
                >
                  Email
                </label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="email@company.com"
                  required
                  autoFocus
                  className="bg-white/[0.05] border border-white/[0.1] text-white placeholder:text-white/25 focus:border-indigo-500/60 focus:ring-0 focus:ring-offset-0 rounded-lg"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-400 hover:to-cyan-400 text-white font-medium transition-all duration-200 hover:shadow-[0_0_20px_rgba(99,102,241,0.35)] border-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </form>
          )}

          {mode === "forgot-sent" && (
            <div className="space-y-5 text-center">
              <div className="w-12 h-12 rounded-full bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center mx-auto">
                <svg
                  className="w-6 h-6 text-indigo-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white/80 mb-1">Check your email</p>
                <p className="text-xs text-white/40">
                  We sent a reset link to <span className="text-white/60">{forgotEmail}</span>. It
                  expires in 1 hour.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setError(null);
                }}
                className="text-xs text-indigo-400/70 hover:text-indigo-400 transition-colors"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-white/20 text-xs text-center mt-6">
          © 2026 DiscoRun. All rights reserved.
        </p>
      </div>
    </div>
  );
}
